import type { CatalogRef, MachineLayout, PxpfTransition, StateMachine } from "@pixelpuf/schemas/pxpf";
import { collectStateIds } from "@pixelpuf/schemas/pxpf";
import { createVisualGraph, type VisualGraph } from "@statelyai/graph";
import { NODE_HEIGHT, NODE_WIDTH } from "./starter.ts";

/**
 * Source-of-truth converters between our persisted `StateMachine + MachineLayout`
 * shape (the .pxpf / store format) and `@statelyai/graph`'s `VisualGraph` (used
 * for algorithms + xyflow rendering).
 *
 * Why two layers?
 *   - `StateMachine` is flat & JSON-serializable — survives round-trips through
 *     the file format and `localStorage` without adapters.
 *   - `VisualGraph` carries the field shape `@statelyai/graph` expects, lets us
 *     run pure algorithms (cycle detection, reachability, topo sort) and pipe
 *     into xyflow via the official converter.
 *
 * All `apply*` mutators are pure: they take a `StateMachine` and return a new
 * one, never mutating in place. Layout mutators take/return `MachineLayout`.
 * Used by store reducers (Phase 2) and the editor's gesture handlers (this file
 * exports them for both).
 */

/** Index signature on NodeData/EdgeData satisfies xyflow's `Node<T>` /
 * `Edge<T>` generic constraint of `T extends Record<string, unknown>`. The
 * declared optional fields stay precisely typed; the index signature only
 * lets callers attach extra unknown keys (which we don't, but xyflow's type
 * system insists on the structural shape). */
export interface NodeData {
  isInitial: boolean;
  entry?: CatalogRef[];
  exit?: CatalogRef[];
  [key: string]: unknown;
}

export interface EdgeData {
  event: string;
  trigger: "onLoopEnd" | "manual";
  guard?: CatalogRef;
  actions?: CatalogRef[];
  /** Index in the source `StateMachine.transitions[]` — the only stable ref to
   * the underlying transition since (from, event) is not unique by design. */
  transitionIndex: number;
  [key: string]: unknown;
}

const DEFAULT_NODE_POSITION = { x: 0, y: 0 } as const;

/** Build a VisualGraph from a StateMachine + MachineLayout. Pure. */
export function toVisualGraph(
  machine: StateMachine,
  layout: MachineLayout,
): VisualGraph<NodeData, EdgeData> {
  const stateIds = collectStateIds(machine);
  const stateMetaById = new Map<string, { entry?: CatalogRef[]; exit?: CatalogRef[] }>();
  if (machine.states) {
    for (const s of machine.states) {
      const meta: { entry?: CatalogRef[]; exit?: CatalogRef[] } = {};
      if (s.entry) meta.entry = s.entry;
      if (s.exit) meta.exit = s.exit;
      stateMetaById.set(s.id, meta);
    }
  }

  const nodes = [...stateIds].map((id) => {
    const pos = layout[id] ?? DEFAULT_NODE_POSITION;
    const meta = stateMetaById.get(id);
    const data: NodeData = { isInitial: id === machine.initial };
    if (meta?.entry) data.entry = meta.entry;
    if (meta?.exit) data.exit = meta.exit;
    return {
      id,
      label: id,
      x: pos.x,
      y: pos.y,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      data,
    };
  });

  const edges = machine.transitions.map((t, i) => {
    const data: EdgeData = { event: t.event, trigger: t.trigger, transitionIndex: i };
    if (t.guard) data.guard = t.guard;
    if (t.actions) data.actions = t.actions;
    return {
      id: edgeIdFor(i),
      sourceId: t.from,
      targetId: t.to,
      label: t.event,
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      data,
    };
  });

  return createVisualGraph<NodeData, EdgeData>({
    direction: "right",
    nodes,
    edges,
  });
}

/** Stable edge id derivation. Uses transition index since (from, event) is
 * not unique. Survives reordering: transition.delete shifts later indices, the
 * editor re-renders with new ids. */
export function edgeIdFor(transitionIndex: number): string {
  return `t${transitionIndex}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pure mutators on StateMachine — return new objects, never mutate in place.
// Each is unit-testable in isolation; used by store reducers and editor handlers.
// ─────────────────────────────────────────────────────────────────────────────

/** Add a new state. No-op if `id` already exists. */
export function applyAddState(m: StateMachine, id: string): StateMachine {
  if (collectStateIds(m).has(id)) return m;
  const states = [...(m.states ?? []), { id }];
  return { ...m, states };
}

/** Rename a state. Rewrites every transition source/target and the initial pointer. */
export function applyRenameState(m: StateMachine, oldId: string, newId: string): StateMachine {
  if (oldId === newId) return m;
  if (!collectStateIds(m).has(oldId)) return m;
  if (collectStateIds(m).has(newId)) return m; // collision — caller should validate
  const states = (m.states ?? []).map((s) => (s.id === oldId ? { ...s, id: newId } : s));
  const transitions = m.transitions.map((t) => ({
    ...t,
    from: t.from === oldId ? newId : t.from,
    to: t.to === oldId ? newId : t.to,
  }));
  const initial = m.initial === oldId ? newId : m.initial;
  return { ...m, initial, states, transitions };
}

/** Delete a state. Prunes incoming and outgoing transitions. If the deleted id
 * was `initial`, picks a deterministic new initial: first explicit state node
 * if any survive, else first alphabetically among any remaining transition
 * endpoints, else `""` (caller should handle the empty case). */
export function applyDeleteState(m: StateMachine, id: string): StateMachine {
  const states = (m.states ?? []).filter((s) => s.id !== id);
  const transitions = m.transitions.filter((t) => t.from !== id && t.to !== id);
  let initial = m.initial;
  if (initial === id) {
    if (states.length > 0) {
      initial = states[0]?.id ?? "";
    } else {
      const fromTransitions = new Set<string>();
      for (const t of transitions) {
        fromTransitions.add(t.from);
        fromTransitions.add(t.to);
      }
      const sorted = [...fromTransitions].sort();
      initial = sorted[0] ?? "";
    }
  }
  return { ...m, initial, states, transitions };
}

/** Set the initial state. No-op if `id` is not a known state. */
export function applySetInitial(m: StateMachine, id: string): StateMachine {
  if (!collectStateIds(m).has(id)) return m;
  if (m.initial === id) return m;
  return { ...m, initial: id };
}

/** Set entry actions for a state (replaces existing). Adds an entry to `states[]`
 * if the state was previously implicit (only mentioned in transitions). */
export function applySetEntryActions(m: StateMachine, id: string, actions: CatalogRef[]): StateMachine {
  if (!collectStateIds(m).has(id)) return m;
  const existing = m.states ?? [];
  if (existing.some((s) => s.id === id)) {
    const states = existing.map((s) => (s.id === id ? { ...s, entry: actions.length > 0 ? actions : undefined } : s));
    return { ...m, states };
  }
  return { ...m, states: [...existing, { id, entry: actions.length > 0 ? actions : undefined }] };
}

/** Set exit actions for a state. Mirror of `applySetEntryActions`. */
export function applySetExitActions(m: StateMachine, id: string, actions: CatalogRef[]): StateMachine {
  if (!collectStateIds(m).has(id)) return m;
  const existing = m.states ?? [];
  if (existing.some((s) => s.id === id)) {
    const states = existing.map((s) => (s.id === id ? { ...s, exit: actions.length > 0 ? actions : undefined } : s));
    return { ...m, states };
  }
  return { ...m, states: [...existing, { id, exit: actions.length > 0 ? actions : undefined }] };
}

/** Append a new transition. Does NOT validate target existence — `validate.ts`
 * + `buildUserMachine` enforce that at runtime; the editor lets users compose
 * intermediate-invalid states freely. */
export function applyAddTransition(m: StateMachine, transition: PxpfTransition): StateMachine {
  return { ...m, transitions: [...m.transitions, transition] };
}

/** Patch a transition by index. Unknown index is a no-op. */
export function applyUpdateTransition(
  m: StateMachine,
  index: number,
  patch: Partial<PxpfTransition>,
): StateMachine {
  if (index < 0 || index >= m.transitions.length) return m;
  const transitions = m.transitions.slice();
  const existing = transitions[index];
  if (!existing) return m;
  transitions[index] = { ...existing, ...patch };
  return { ...m, transitions };
}

/** Delete a transition by index. Unknown index is a no-op. */
export function applyDeleteTransition(m: StateMachine, index: number): StateMachine {
  if (index < 0 || index >= m.transitions.length) return m;
  const transitions = m.transitions.filter((_, i) => i !== index);
  return { ...m, transitions };
}

/** Update a single state's xyflow position in MachineLayout. */
export function applyMoveState(layout: MachineLayout, id: string, position: { x: number; y: number }): MachineLayout {
  return { ...layout, [id]: position };
}
